import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

// ── Instructions modal — large, reader-friendly ───────────────────────────────
function InstructionsModal({ exam, onClose, onStart }) {
  return (
    <div className="modal-overlay" style={{ padding: '20px' }}>
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        width: '100%',
        maxWidth: 860,
        maxHeight: '92vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 28px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Exam Instructions</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0, lineHeight: 1.3 }}>{exam.title}</h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 24, padding: '4px 8px', borderRadius: 6, lineHeight: 1 }}>✕</button>
        </div>

        {/* Exam meta chips */}
        <div style={{ padding: '14px 28px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { icon:'⏱', label:'Duration',  val:`${exam.duration_minutes} minutes`, color:'var(--accent-primary)' },
            { icon:'❓', label:'Questions', val:`${exam.question_count} questions`,  color:'var(--accent-secondary)' },
            { icon:'🎯', label:'Marks',     val:`${exam.total_marks} total`,          color:'var(--accent-warning)' },
            { icon:'✅', label:'Pass',      val:`${exam.passing_marks} to pass`,       color:'var(--accent-success)' },
          ].map(m => (
            <div key={m.label} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', background:'var(--bg-elevated)', borderRadius:10, border:'1px solid var(--border)' }}>
              <span style={{ fontSize:18 }}>{m.icon}</span>
              <div>
                <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.6px' }}>{m.label}</div>
                <div style={{ fontSize:13, fontWeight:700, color:m.color }}>{m.val}</div>
              </div>
            </div>
          ))}
          {exam.proctoring_enabled && (
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', background:'rgba(245,158,11,0.1)', borderRadius:10, border:'1px solid rgba(245,158,11,0.3)' }}>
              <span style={{ fontSize:18 }}>🔒</span>
              <div>
                <div style={{ fontSize:10, color:'var(--accent-warning)', textTransform:'uppercase', letterSpacing:'0.6px' }}>Proctoring</div>
                <div style={{ fontSize:13, fontWeight:700, color:'var(--accent-warning)' }}>AI Monitored</div>
              </div>
            </div>
          )}
        </div>

        {/* Instructions body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>
          {exam.instructions ? (
            <div style={{ fontSize: 15, lineHeight: 2, color: 'var(--text-primary)' }}
              dangerouslySetInnerHTML={{ __html: exam.instructions }} />
          ) : (
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.8px' }}>General Guidelines</div>
              {[
                ['📸', 'Camera',       'Ensure your face is clearly visible in the camera throughout the exam'],
                ['🖥️', 'Fullscreen',  'Stay in fullscreen mode. Exiting fullscreen will be flagged as a violation'],
                ['🚫', 'Tab Switching', 'Do not switch tabs, open new windows, or minimize the browser'],
                ['👁️', 'Face Detection', 'Keep your face visible at all times. Looking away repeatedly may result in termination'],
                ['🔇', 'No Assistance', 'Do not use external help, books, or other devices during the exam'],
                ['⏳', 'Time',          `You have ${exam.duration_minutes} minutes. The timer starts immediately when you begin`],
              ].map(([icon, title, desc]) => (
                <div key={title} style={{ display:'flex', gap:14, marginBottom:18, padding:'12px 16px', background:'var(--bg-elevated)', borderRadius:10 }}>
                  <span style={{ fontSize:22, flexShrink:0 }}>{icon}</span>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, marginBottom:3 }}>{title}</div>
                    <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {exam.min_submit_minutes > 0 && (
            <div style={{ marginTop:20, padding:'14px 18px', background:'rgba(245,158,11,0.1)', borderRadius:10, border:'1px solid rgba(245,158,11,0.3)', display:'flex', gap:12, alignItems:'center' }}>
              <span style={{ fontSize:24 }}>⏳</span>
              <div>
                <div style={{ fontWeight:700, color:'var(--accent-warning)', marginBottom:2 }}>Early Submission Locked</div>
                <div style={{ fontSize:13, color:'var(--text-secondary)' }}>
                  You cannot submit the exam for the first <strong>{exam.min_submit_minutes} minutes</strong>.
                </div>
              </div>
            </div>
          )}

          {/* Date/time notice */}
          {exam.start_time && (
            <div style={{ marginTop:16, padding:'14px 18px', background:'rgba(79,142,247,0.08)', borderRadius:10, border:'1px solid rgba(79,142,247,0.2)', display:'flex', gap:12, alignItems:'center' }}>
              <span style={{ fontSize:24 }}>📅</span>
              <div>
                <div style={{ fontWeight:700, color:'var(--accent-primary)', marginBottom:2 }}>Scheduled Start</div>
                <div style={{ fontSize:13, color:'var(--text-secondary)' }}>
                  Exam starts: <strong>{new Date(exam.start_time).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true })} IST</strong>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={{ padding: '16px 28px', borderTop: '1px solid var(--border)', display: 'flex', gap: 12 }}>
          <button className="btn btn-secondary" style={{ justifyContent: 'center' }} onClick={onClose}>
            Close
          </button>
          {onStart && (
            exam?.proctor_published === false ? (
              <div style={{ flex: 1, padding: '12px 20px', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 22 }}>⏳</span>
                <div>
                  <div style={{ fontWeight: 700, color: 'var(--accent-warning)', fontSize: 14 }}>Waiting for Proctor</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>The proctor has not opened this exam yet. You'll be notified when it starts.</div>
                </div>
              </div>
            ) : (
              <button className="btn btn-primary btn-lg" style={{ flex: 1, justifyContent: 'center', fontSize: 15, fontWeight: 700 }} onClick={onStart}>
                🚀 Start Exam Now →
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main StudentDashboard ────────────────────────────────────────────────────
export default function StudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [exams,         setExams]         = useState([]);
  const [stats,         setStats]         = useState({});
  const [attempts,      setAttempts]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [activeTab,     setActiveTab]     = useState('available');
  const [instrExam,     setInstrExam]     = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs,    setShowNotifs]    = useState(false);

  const unread = notifications.filter(n => !n.is_read).length;

  const fetchData = useCallback(() => {
    Promise.all([
      axios.get('/api/exams'),
      axios.get('/api/attempts/stats/dashboard'),
      axios.get('/api/attempts/my'),
      axios.get('/api/attempts/notifications'),
    ]).then(([exRes, stRes, atRes, notifRes]) => {
      const examList = exRes.data;
      setExams(examList);
      examList.forEach(ex => {
        axios.post(`/api/exams/${ex.id}/event`, { event_type: 'logged_in' }).catch(()=>{});
      });
      setStats(stRes.data);
      setAttempts(atRes.data);
      setNotifications(notifRes.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Real-time WS for exam_published
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) return;
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const apiHost = process.env.NODE_ENV === 'development'
      ? `${window.location.hostname}:5000`
      : (process.env.REACT_APP_API_HOST || window.location.hostname);
    let ws;
    try {
      ws = new WebSocket(`${proto}://${apiHost}/ws?token=${token}&role=student_dashboard`);
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'exam_published' && msg.published) {
            fetchData();
            toast.success(`🟢 "${msg.examTitle}" is now open!`, { autoClose:8000 });
          }
        } catch {}
      };
    } catch {}
    return () => ws?.close();
  }, [fetchData]);

  const handleReadInstructions = (exam) => {
    setInstrExam(exam);
    axios.post(`/api/exams/${exam.id}/event`, { event_type: 'instructions_opened' }).catch(()=>{});
  };

  const goToExam = (examId) => {
    // Find the exam to check if proctoring is needed
    const exam = exams.find(e => e.id === examId);
    if (exam && !exam.proctoring_enabled && !exam.require_fullscreen) {
      // Non-proctored exam — skip permission checks, go straight to exam
      navigate(`/exam/${examId}`, { state: { examId } });
    } else {
      // Proctored exam — must pass camera/mic/fullscreen checks first
      navigate('/exam/check', { state: { examId } });
    }
  };

  const handleStartFromInstructions = () => {
    if (!instrExam) return;
    const id = instrExam.id;
    setInstrExam(null);
    goToExam(id);
  };

  const markNotifsRead = async () => {
    await axios.post('/api/attempts/notifications/read').catch(() => {});
    setNotifications(p => p.map(n => ({ ...n, is_read: true })));
  };

  const getStatusBadge = (exam) => {
    if (exam.my_attempt_status === 'submitted')   return <span className="badge badge-green">Completed</span>;
    if (exam.my_attempt_status === 'in_progress') return <span className="badge badge-yellow">In Progress</span>;
    if (exam.my_attempt_status === 'terminated')  return <span className="badge badge-red">Terminated</span>;
    return <span className="badge badge-blue">Not Started</span>;
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="layout">
      <Sidebar />
      <main className="main-content">

        {/* Header */}
        <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:4 }}>
              {user?.photo_url ? (
                <img src={user.photo_url} alt="profile"
                  style={{ width:52, height:52, borderRadius:26, objectFit:'cover', border:'3px solid var(--accent-primary)', flexShrink:0 }} />
              ) : (
                <div style={{ width:52, height:52, borderRadius:26, background:'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:22, flexShrink:0 }}>
                  {user?.full_name?.[0]?.toUpperCase() || 'S'}
                </div>
              )}
              <h1 className="page-title">Welcome back, {user?.full_name?.split(' ')[0]} 👋</h1>
            </div>
            <p className="page-subtitle">Your exam dashboard</p>
          </div>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            {/* Notification bell */}
            <div style={{ position:'relative' }}>
              <button className="btn btn-secondary"
                onClick={() => { setShowNotifs(v => !v); if (unread > 0) markNotifsRead(); }}>
                🔔{unread > 0 && <span style={{
                  marginLeft:6, background:'var(--accent-danger)', color:'#fff',
                  borderRadius:10, padding:'0 5px', fontSize:11, fontWeight:700,
                }}>{unread}</span>}
              </button>
              {showNotifs && (
                <div style={{
                  position:'absolute', right:0, top:'calc(100% + 8px)', zIndex:300,
                  background:'var(--bg-card)', border:'1px solid var(--border)',
                  borderRadius:12, width:340, maxHeight:380,
                  overflowY:'auto', boxShadow:'0 8px 32px rgba(0,0,0,0.4)',
                }}>
                  <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', fontWeight:600, fontSize:14 }}>Notifications</div>
                  {notifications.length === 0
                    ? <div style={{ padding:24, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>No notifications yet</div>
                    : notifications.map(n => (
                      <div key={n.id} style={{
                        padding:'10px 16px', borderBottom:'1px solid var(--border)',
                        background: !n.is_read ? 'rgba(79,142,247,0.05)' : 'transparent',
                        cursor: n.exam_id ? 'pointer' : 'default',
                      }} onClick={() => { if (n.exam_id) { setShowNotifs(false); const ex=exams.find(e=>e.id===n.exam_id); if(ex) handleReadInstructions(ex); } }}>
                        <div style={{ fontSize:13, fontWeight: !n.is_read?600:400 }}>{n.title}</div>
                        <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:2 }}>{n.message.slice(0,80)}...</div>
                        <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:3 }}>{format(new Date(n.created_at),'dd MMM, HH:mm')}</div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="card-grid card-grid-4 mb-6">
          {[
            { val:exams.length,                label:'My Exams',     color:'var(--accent-primary)'   },
            { val:stats.total_attempts||0,     label:'Attempts',     color:'var(--accent-secondary)' },
            { val:stats.passed||0,             label:'Passed',       color:'var(--accent-success)'   },
            { val:`${stats.total_attempts > 0 ? Math.round((stats.passed/stats.total_attempts)*100) : 0}%`, label:'Pass Rate', color:'var(--accent-warning)' },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div className="stat-value" style={{ color:s.color }}>{s.val}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:8, marginBottom:20 }}>
          {[['available','◉ Available Exams'],['history','◈ My Results']].map(([tab,label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)} className="btn btn-secondary btn-sm"
              style={activeTab===tab ? { borderColor:'var(--accent-primary)', color:'var(--accent-primary)', background:'rgba(79,142,247,0.1)' } : {}}>
              {label}
            </button>
          ))}
        </div>

        {/* Available exams */}
        {activeTab === 'available' && (
          <div className="card-grid card-grid-2">
            {exams.length === 0 && (
              <div className="card" style={{ gridColumn:'1/-1', textAlign:'center', padding:48 }}>
                <div style={{ fontSize:32, marginBottom:12 }}>📋</div>
                <div style={{ color:'var(--text-secondary)', marginBottom:16 }}>No exams available yet.</div>
                <div style={{ fontSize:13, color:'var(--text-muted)' }}>
                  If you have a registration link, use it to enroll:<br/>
                  <code style={{ fontFamily:'var(--font-mono)', color:'var(--accent-primary)' }}>
                    {window.location.origin}/register/&lt;token&gt;
                  </code>
                </div>
              </div>
            )}
            {exams.map(exam => (
              <div key={exam.id} className="exam-card">
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                  <h3 style={{ fontWeight:700, fontSize:15, margin:0, flex:1 }}>{exam.title}</h3>
                  {getStatusBadge(exam)}
                </div>
                {exam.description && (
                  <p style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:12, lineHeight:1.5 }}>
                    {exam.description.slice(0,100)}{exam.description.length > 100 ? '...' : ''}
                  </p>
                )}
                <div style={{ display:'flex', gap:12, flexWrap:'wrap', fontSize:12, color:'var(--text-secondary)', marginBottom:14 }}>
                  {exam.org_name && <span style={{ fontWeight:600, color:'var(--accent-secondary)' }}>🏢 {exam.org_name}</span>}
                  <span>⏱ {exam.duration_minutes}min</span>
                  <span>❓ {exam.question_count} Qs</span>
                  <span>🎯 {exam.total_marks} marks</span>
                  {exam.proctoring_enabled && <span style={{ color:'var(--accent-warning)' }}>🔒 Proctored</span>}
                  {!exam.proctor_published && <span style={{ color:'var(--accent-warning)' }}>⏳ Not yet open</span>}
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleReadInstructions(exam)}>
                    📋 Instructions
                  </button>
                  {exam.my_attempt_status === 'submitted' ? (
                    <button className="btn btn-success btn-sm" onClick={async () => {
                      // FIX 7: check permission before navigating to result
                      try {
                        const cfg = await axios.get(`/api/exams/${exam.id}/settings-public`);
                        if (cfg.data.student_can_download_result === false) {
                          toast.info('Result is currently unavailable. Please contact your organization.');
                          return;
                        }
                      } catch { /* allow on error */ }
                      navigate(`/result/${exam.my_attempt_id}`);
                    }}>
                      View Result — {exam.my_score}/{exam.total_marks}
                    </button>
                  ) : exam.my_attempt_status === 'in_progress' ? (
                    <button className="btn btn-primary btn-sm" onClick={() => goToExam(exam.id)}>Resume →</button>
                  ) : exam.my_attempt_status === 'terminated' ? (
                    <span className="badge badge-red" style={{ alignSelf:'center' }}>Terminated</span>
                  ) : exam.proctor_published === false ? (
                    <button className="btn btn-secondary btn-sm" disabled style={{ opacity:0.5, cursor:'not-allowed' }}>
                      ⏳ Waiting for Proctor
                    </button>
                  ) : (
                    <button className="btn btn-primary btn-sm" onClick={() => goToExam(exam.id)}>
                      {exam.proctoring_enabled ? '🔒 Start Proctored Exam →' : 'Start Exam →'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* History tab */}
        {activeTab === 'history' && (
          <div className="card">
            <div className="table-container" style={{ border:'none' }}>
              <table>
                <thead>
                  <tr><th>Exam</th><th>Status</th><th>Score</th><th>Pass</th><th>Date</th><th></th></tr>
                </thead>
                <tbody>
                  {attempts.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign:'center', color:'var(--text-secondary)', padding:32 }}>No attempts yet</td></tr>
                  )}
                  {attempts.map(a => (
                    <tr key={a.id}>
                      <td style={{ fontWeight:500 }}>{a.exam_title}</td>
                      <td><span className={`badge badge-${a.status==='submitted'?'green':a.status==='terminated'?'red':'yellow'}`}>{a.status}</span></td>
                      <td style={{ fontFamily:'var(--font-mono)' }}>{a.score ?? '—'}/{a.total_marks}</td>
                      <td>{a.passed===true ? '✅' : a.passed===false ? '❌' : '—'}</td>
                      <td style={{ fontSize:13, color:'var(--text-secondary)' }}>{format(new Date(a.created_at),'dd MMM yyyy')}</td>
                      <td>{a.status==='submitted' && <button className="btn btn-secondary btn-sm" onClick={()=>navigate(`/result/${a.id}`)}>View →</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {instrExam && (
        <InstructionsModal
          exam={instrExam}
          onClose={() => setInstrExam(null)}
          onStart={instrExam.my_attempt_status !== 'submitted' && instrExam.my_attempt_status !== 'terminated'
            ? handleStartFromInstructions : null}
        />
      )}
    </div>
  );
}
